import { useEffect, useRef, useState } from "react";
import { Check, Loader2, ZoomIn } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const VIEW = 280;
const OUT = 256;

export function AvatarCropDialog({ file, onCancel, onCropped }) {
  const [src, setSrc] = useState(null);
  const [img, setImg] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [busy, setBusy] = useState(false);
  const dragRef = useRef(null);

  useEffect(() => {
    if (!file) {
      setSrc(null);
      setImg(null);
      setZoom(1);
      setOffset({ x: 0, y: 0 });
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setSrc(url);
    const image = new Image();
    image.onload = () => setImg(image);
    image.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  if (!file) return null;

  const baseScale = img ? VIEW / Math.min(img.naturalWidth, img.naturalHeight) : 1;
  const s = baseScale * zoom;
  const dispW = img ? img.naturalWidth * s : VIEW;
  const dispH = img ? img.naturalHeight * s : VIEW;

  const clamp = (o, z = zoom) => {
    if (!img) return o;
    const sc = baseScale * z;
    const maxX = Math.max(0, (img.naturalWidth * sc - VIEW) / 2);
    const maxY = Math.max(0, (img.naturalHeight * sc - VIEW) / 2);
    return { x: Math.min(maxX, Math.max(-maxX, o.x)), y: Math.min(maxY, Math.max(-maxY, o.y)) };
  };

  const onPointerDown = (e) => {
    dragRef.current = { startX: e.clientX, startY: e.clientY, ox: offset.x, oy: offset.y };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    const d = dragRef.current;
    setOffset(clamp({ x: d.ox + (e.clientX - d.startX), y: d.oy + (e.clientY - d.startY) }));
  };
  const onPointerUp = () => {
    dragRef.current = null;
  };

  const changeZoom = (z) => {
    setZoom(z);
    setOffset((o) => clamp(o, z));
  };

  const save = async () => {
    if (!img) return;
    setBusy(true);
    try {
      const left = VIEW / 2 - dispW / 2 + offset.x;
      const top = VIEW / 2 - dispH / 2 + offset.y;
      const sx = -left / s;
      const sy = -top / s;
      const sw = VIEW / s;
      const canvas = document.createElement("canvas");
      canvas.width = OUT;
      canvas.height = OUT;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, sx, sy, sw, sw, 0, 0, OUT, OUT);
      const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.85));
      await onCropped(blob);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="sm:max-w-sm" data-testid="avatar-crop-dialog">
        <DialogHeader>
          <DialogTitle>Profilbild anpassen</DialogTitle>
          <DialogDescription>Bild mit dem Finger/der Maus verschieben und zoomen.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center gap-4">
          <div
            data-testid="avatar-crop-area"
            className="relative overflow-hidden rounded-full border-4 border-neutral-200 cursor-move touch-none select-none bg-neutral-100"
            style={{ width: VIEW, height: VIEW }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
          >
            {src && (
              <img
                src={src}
                alt=""
                draggable={false}
                className="absolute max-w-none pointer-events-none"
                style={{
                  width: dispW,
                  height: dispH,
                  left: VIEW / 2 - dispW / 2 + offset.x,
                  top: VIEW / 2 - dispH / 2 + offset.y,
                }}
              />
            )}
          </div>
          <div className="w-full flex items-center gap-3">
            <ZoomIn className="h-4 w-4 text-neutral-400 shrink-0" />
            <input
              data-testid="avatar-zoom-slider"
              type="range"
              min="1"
              max="3"
              step="0.01"
              value={zoom}
              onChange={(e) => changeZoom(parseFloat(e.target.value))}
              className="flex-1 accent-blue-600"
            />
          </div>
          <div className="w-full flex gap-2">
            <Button variant="outline" className="flex-1" onClick={onCancel} data-testid="avatar-crop-cancel">
              Abbrechen
            </Button>
            <Button className="flex-1" onClick={save} disabled={busy || !img} data-testid="avatar-crop-save">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
              Übernehmen
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
